/**
 * ClearIntake Auth Context
 *
 * Provides JWT-based auth state to the entire app.
 * JWT is stored in React state (memory only — never in localStorage per HIPAA).
 *
 * Usage:
 *   <AuthProvider><App /></AuthProvider>
 *
 *   const { user, isStaff, isContact, login, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { setAuthToken, clearAuthToken, auth } from './api.js';
import { extractSSOToken, requestGHLUserData } from './ghl-sso.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StaffUser {
  role: 'staff';
  locationId: string;
  userId: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isAgencyAdmin: boolean;
}

interface ContactUser {
  role: 'contact';
  locationId: string;
  contactGhlId: string;
}

type User = StaffUser | ContactUser;

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isStaff: boolean;
  isContact: boolean;
  isAdmin: boolean;
  isAgencyAdmin: boolean;
  login: (token: string) => void;
  loginWithSSO: () => Promise<void>;
  loginWithOTP: (magicToken: string, otp: string) => Promise<{ entityType: string; entityId: string }>;
  logout: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthState | null>(null);

/**
 * Decode a JWT payload without verification (client-side only — server verifies).
 */
function decodeJWTPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT format');
  const payload = parts[1];
  if (!payload) throw new Error('Invalid JWT: missing payload');
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json) as Record<string, unknown>;
}

function parseUser(payload: Record<string, unknown>): User {
  if (payload['role'] === 'staff') {
    return {
      role: 'staff',
      locationId: payload['locationId'] as string,
      userId: payload['userId'] as string,
      email: payload['email'] as string,
      name: payload['name'] as string,
      isAdmin: payload['isAdmin'] === true,
      isAgencyAdmin: payload['isAgencyAdmin'] === true,
    };
  }
  return {
    role: 'contact',
    locationId: payload['locationId'] as string,
    contactGhlId: payload['contactGhlId'] as string,
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const login = useCallback((token: string) => {
    setAuthToken(token);
    try {
      const payload = decodeJWTPayload(token);
      setUser(parseUser(payload));
      setError(null);

      // Schedule refresh 5 minutes before expiry
      const exp = payload['exp'] as number | undefined;
      if (exp) {
        const msUntilRefresh = (exp * 1000 - Date.now()) - 5 * 60 * 1000;
        if (msUntilRefresh > 0 && refreshTimerRef.current === null) {
          refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            // For SSO staff sessions: re-run the SSO exchange. Prefer the secure
            // encrypted postMessage payload; fall back to a legacy ?ssoToken= URL
            // param if present. Contacts have short, non-refreshing sessions.
            void (async () => {
              let encrypted: string | null = null;
              try {
                encrypted = await requestGHLUserData();
              } catch {
                encrypted = extractSSOToken();
              }
              if (!encrypted) return; // will get 401 on next API call
              try {
                const res = await auth.ghlSSO(encrypted);
                if (res.success && res.data) login(res.data.token);
              } catch { /* will get 401 on next API call */ }
            })();
          }, msUntilRefresh);
        }
      }
    } catch {
      setError('Invalid token');
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    setUser(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const loginWithSSO = useCallback(async () => {
    // Primary, secure path: ask the GHL parent window (via postMessage) for the
    // ENCRYPTED user-data payload. The backend decrypts + validates it and
    // derives the location/user — a bare locationId is no longer trusted.
    //
    // Fallback ONLY: if a GHL surface still appends ?ssoToken= to the URL, use it.
    // Both feed /api/auth/ghl-sso, which accepts the encrypted blob under ssoToken.
    let encrypted: string | null = null;
    try {
      encrypted = await requestGHLUserData();
    } catch {
      // postMessage unavailable / timed out — fall through to the URL fallback.
      encrypted = extractSSOToken();
    }

    if (!encrypted) {
      // No secure payload available. Do NOT fall back to a bare locationId — the
      // backend rejects it, and minting a session from a non-secret id is unsafe.
      setLoading(false);
      return;
    }

    try {
      const res = await auth.ghlSSO(encrypted);
      if (res.success && res.data) {
        login(res.data.token);
      } else {
        setError(res.error ?? 'SSO authentication failed');
      }
    } catch {
      setError('Failed to authenticate');
    } finally {
      setLoading(false);
    }
  }, [login]);

  const loginWithOTP = useCallback(async (
    magicToken: string,
    otp: string,
  ): Promise<{ entityType: string; entityId: string }> => {
    const res = await auth.verifyOTP(magicToken, otp);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'OTP verification failed');
    }
    login(res.data.token);
    return { entityType: res.data.entityType, entityId: res.data.entityId };
  }, [login]);

  // Auto-trigger SSO on mount.
  //
  // The secure mechanism is the encrypted postMessage handshake, which is only
  // available when we are inside a GHL iframe (window.parent !== window). We also
  // honor a legacy ?ssoToken= URL param as a fallback surface. A bare locationId
  // is NOT a trigger — the backend rejects it and we must not mint a session
  // from a non-secret id. Direct (non-iframe) browser navigation simply stops
  // loading and shows the unauthorized state.
  useEffect(() => {
    const params      = new URLSearchParams(window.location.search);
    const inIframe    = window.parent !== window;
    const hasSSOToken = !!(params.get('ssoToken') || '');

    if (inIframe || hasSSOToken) {
      loginWithSSO();
    } else {
      setLoading(false);
    }
  }, [loginWithSSO]);

  const value: AuthState = {
    user,
    loading,
    error,
    isStaff: user?.role === 'staff',
    isContact: user?.role === 'contact',
    isAdmin: user?.role === 'staff' && user.isAdmin === true,
    isAgencyAdmin: user?.role === 'staff' && user.isAgencyAdmin === true,
    login,
    loginWithSSO,
    loginWithOTP,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
